import React, { useContext, useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { authAtom } from "../../states/auth";
import { AppContext } from "../../contexts/AppContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { SignOutIcon } from "../SVGIcon";
import classNames from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";
import Navbar from "react-bootstrap/Navbar";
import Dropdown from "react-bootstrap/Dropdown";
import Image from "react-bootstrap/Image";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  HomeIcon,
  HomeFillIcon,
  SupportCenterIcon,
  SupportCenterFillIcon,
  HistoryIcon,
  HistoryFillIcon,
  ClientAccessIcon,
  ClientAccessFillIcon,
  UsersIcon,
  UsersFillIcon,
  RoutingIcon,
  RoutingFillIcon,
  ReportHubIcon,
  ReportHubFillIcon,
  WikiIcon,
  WikiFillIcon,
  InboxIcon,
  InboxFillIcon,
  CalendarIcon,
  CalendarFillIcon,
  ChatIcon,
  ChatFillIcon,
  MenuIcon,
  ThreeDotsIcon,
} from "./SidebarIcons";
import "./Sidebar.scss";
import { chatUnseenCountsAtom, totalChatUnreadAtom } from "../../states/chatAtoms";
import { getExpertChatList } from "../../services/chatServices";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(window.innerWidth > 1199);
  const [auth, setAuth] = useAtom(authAtom);
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [, setUnseenCounts] = useAtom(chatUnseenCountsAtom);

  useEffect(() => {
    console.log("🔥 useEffect running in ChatUnreadInitializer");
    const safeFilter = {
      filter: "all",   
      search: "",
      no_of_result: 0,
      page: 0 
    };
    const loadInitialCounts = async () => {
      try {
        const response = await getExpertChatList(safeFilter);
        const conversations = response?.data || [];
        const countsFromApi = {};
        conversations.forEach((conv) => {
          const unread = conv.unreadCount || 0;
          if (unread > 0) {
            countsFromApi[conv.id] = unread;
          }
        });

        setUnseenCounts(countsFromApi);
      } catch (error) {
        console.error("❌ API call failed:", error);
        console.error("Error details:", error.response || error.message);
      }
    };

    loadInitialCounts();
  }, [setUnseenCounts]);
  const totalChatUnread = useAtomValue(totalChatUnreadAtom);
  const location = useLocation();
  const isOnChatPage = location.pathname.startsWith('/chat');

  const displayCount = isOnChatPage ? 0 : totalChatUnread;
  useEffect(() => {
    const handleResize = () => {
      setIsOpen(window.innerWidth > 1199);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const SidebarToggle = () => {
    setIsOpen(!isOpen);
    toggleSidebar();
  };

  const SideBarLink = ({
    to,
    icon: Icon,
    activeIcon: ActiveIcon,
    label,
    count,
    tooltip = true,
    tooltipPlacement = "right",
    className = "",
  }) => {
    const renderLinkContent = (isActive) => (
      <>
        <div className="d-flex align-items-center justify-content-center">
          {isActive && ActiveIcon ? <ActiveIcon /> : Icon && <Icon />}
          <span className="link-label">{label}</span>
        </div>
        {count > 0 && <span className="count">{count}</span>}
      </>
    );

    const renderNavLink = () => (
      <NavLink
        to={to}
        className={({ isActive }) =>
          classNames(
            "nav-link",
            "d-flex",
            "align-items-center",
            "justify-content-between",
            className,
            {
              active: isActive,
            }
          )
        }
      >
        {({ isActive }) => renderLinkContent(isActive)}
      </NavLink>
    );

    if (!tooltip) return renderNavLink();

    return (
      <OverlayTrigger
        placement={tooltipPlacement}
        overlay={
          <Tooltip id={`tooltip-${label}`} className="sidebar-menu-tooltip">
            {label}
          </Tooltip>
        }
      >
        {renderNavLink()}
      </OverlayTrigger>
    );
  };

  // Use the global sidebar state
  const isSidebarCollapsed = isCollapsed;

  const logoutUser = () => {
    setAuth(null);
    // disconnectSocket();
  };

  return (
    <div
      className={`sidebar d-flex flex-column justify-content-between${isSidebarCollapsed ? " collapsed" : ""
        }`}
    >
      <div className="mb-4">
        <Navbar className="sidebar-logo">
          <Navbar.Brand to="/" className="p-0 m-0">
            <Image
              width={168}
              height={32}
              src="/images/assistlink-logo.png"
              alt="Assistlink App Logo"
            />
            <Image
              width={38}
              height={38}
              src="/images/assistlink-icon.png"
              alt="Assistlink App Icon"
            />
          </Navbar.Brand>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={SidebarToggle}
            aria-label="Sidebar Toggle Menu"
          >
            <MenuIcon />
          </button>
        </Navbar>
        <div className="sidebar-menu">
          {auth?.user?.roleId === 2 && (
            <div className="nav-item">
              <SideBarLink
                to="/dashboard"
                icon={HomeIcon}
                activeIcon={HomeFillIcon}
                label="Dashboard"
              />
            </div>
          )}
          {auth?.user?.roleId === 3 && (
            <div className="nav-item">
              <SideBarLink
                to="/inbox"
                icon={InboxIcon}
                activeIcon={InboxFillIcon}
                label="Inbox"
              />
            </div>
          )}
          {auth?.user?.roleId === 3 && (
            <div className="nav-item">
              <SideBarLink
                to="/calendar"
                icon={CalendarIcon}
                activeIcon={CalendarFillIcon}
                label="Calendar"
              />
            </div>
          )}
          {auth?.user?.roleId === 2 && (
            <div className="nav-item">
              <SideBarLink
                to="/support-center"
                icon={SupportCenterIcon}
                activeIcon={SupportCenterFillIcon}
                label="Sessions"
              />
            </div>
          )}
          {auth?.user?.roleId === 3 && (
            <div
              className="nav-item"
              onClick={() => {
                document.body.classList.add("sidebar-collapsed");
              }}
            >
              <SideBarLink
                to="/chat"
                icon={ChatIcon}
                activeIcon={ChatFillIcon}
                label="Chats"
                className="chat-red"
                count={displayCount}
              />
            </div>
          )}
          <div className="nav-item divider" />
          {[3, 2].includes(auth?.user?.roleId) && (
            <div className="nav-item">
              <SideBarLink
                to="/history"
                icon={HistoryIcon}
                activeIcon={HistoryFillIcon}
                label="History"
              />
            </div>
          )}
          <div className="nav-item">
            <SideBarLink
              to="/client-access"
              icon={ClientAccessIcon}
              activeIcon={ClientAccessFillIcon}
              label="Contacts"
            />
          </div>
          {auth?.user?.roleId === 2 && (
            <div className="nav-item">
              <SideBarLink
                to="/users"
                icon={UsersIcon}
                activeIcon={UsersFillIcon}
                label="My Team"
              />
            </div>
          )}
          {auth?.user?.roleId === 2 && (
            <div className="nav-item">
              <SideBarLink
                to="/routing"
                icon={RoutingIcon}
                activeIcon={RoutingFillIcon}
                label="Expertise Areas"
              />
            </div>
          )}
          <div className="nav-item divider" />

          {auth?.user?.roleId === 2 && (
            <div className="nav-item">
              <SideBarLink
                to="/report-hub"
                icon={ReportHubIcon}
                activeIcon={ReportHubFillIcon}
                label="Report Hub"
                count={0}
              />
            </div>
          )}
          {auth?.user?.roleId === 3 && (
            <div className="nav-item">
              <SideBarLink
                to="/report-hub/list"
                icon={ReportHubIcon}
                activeIcon={ReportHubFillIcon}
                label="Report Hub"
                count={0}
              />
            </div>
          )}
          <div className="nav-item">
            <SideBarLink
              to="/wiki"
              icon={WikiIcon}
              activeIcon={WikiFillIcon}
              label="Wiki"
            />
          </div>
        </div>
      </div>
      <Dropdown className={`sidebar-user-menu ${!isOpen ? "close" : ""}`}>
        <Dropdown.Toggle
          variant=""
          className="d-flex align-items-center justify-content-between gap-2 text-start dropdown-toggle-none"
        >
          <div className="d-flex align-items-center gap-2">
            {auth?.user?.profilePic ? (
              <picture className="user-avtar user-avtar-40">
                <Image
                  width="40"
                  height="40"
                  alt="User Avatar"
                  className="object-fit-cover rounded-circle"
                  src={auth?.user?.profilePic}
                />
              </picture>
            ) : (
              <Image
                width="48"
                height="48"
                alt="User Avatar"
                className="object-fit-cover rounded-circle"
                src={`https://ui-avatars.com/api/?name=${auth?.user?.firstName}+${auth?.user?.lastName}&background=007bff&color=fff&size=100`}
              />
            )}

            <p className="mb-0">
              <span className="text-gray-700 text-truncate d-block w-100">
                {auth?.user?.roleId === 2
                  ? auth?.user?.companyName
                  : `${auth?.user?.firstName} ${auth?.user?.lastName}`}
              </span>
              <span className="fs-12 fw-normal text-gray-500 text-truncate d-block">
                {auth?.user?.email}
              </span>
            </p>
          </div>
          <ThreeDotsIcon />
        </Dropdown.Toggle>
        <Dropdown.Menu
          align="start"
          className="mb-1 p-2 border border-gray-200 rounded-3"
        >
          <Dropdown.Item
            as={Link}
            to="/profile-overview"
            className="fs-16 fw-normal text-gray-600 d-flex align-items-center gap-2 mb-2 p-2 w-100 text-gray-700 fw-normal text-decoration-none px-2 py-2 rounded-3 hover-bg-gray-100"
          >
            <Image
              src="/images/user-circle-icon.svg"
              width={24}
              height={24}
              alt={auth?.user?.firstName}
            />{" "}
            Profile Overview
          </Dropdown.Item>
          <Dropdown.Item
            as={Link}
            to="/auth"
            className="fs-16 fw-normal p-2 bg-red-100 text-red-600 fw-normal text-decoration-none rounded-3 px-3 py-2 d-flex align-items-center gap-2"
            onClick={() => logoutUser()}
          >
            <SignOutIcon size={24} /> Logout
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default Sidebar;
